import React, {
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import moment from 'moment';
import Input from '@material-ui/core/Input';
import Send from '@material-ui/icons/Send';
import CancelIcon from '@material-ui/icons/Cancel';
import Request from '~/providers/Request';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import InputControl from '~/components/Input/InputControl';
import InputDate from '~/components/Input/InputDate';
import Button from '~/components/Button';
import InputDropzone from '~/components/Input/InputDropzone';
import Dialog from '~/components/Dialog';
import PushRecord from '~/records/PushRecord';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import { appName } from '../../../../config/uri';

const attrName = (attr) => PushRecord.hummanAttributeName(attr);

const initialRecord = (doctor_schedule) => ({
  imageId: null,
  title:      `Doctor ${appName} 👨‍⚕️`,
  title_en:   `${appName} Doctor 👨‍⚕️`,
  title_es:   `${appName} Doctor 👨‍⚕️`,
  message: ( doctor_schedule ?
    'Clique aqui e converse com o doutor.' :
    'Ops, tentamos contato mas sem sucesso. Se precisar, acione novamente o botão médico. Estamos à disposição para lhe conectar com o doutor 🙂'
  ),
  message_en: ( doctor_schedule ?
    'Click here and talk to the doctor.' :
    'Oops, we tried to contact but without success. If necessary, press the medical button again. We are available to connect you with the doctor 🙂'
  ),
  message_es: ( doctor_schedule ?
    'Haga clic aquí y hable con el médico.' :
    'Vaya, tratamos de contactar pero sin éxito. Si es necesario, presione el botón médico nuevamente. Estamos disponibles para conectarlo con el médico 🙂'
  ),
  link_url: doctor_schedule ? 'whereby.com/geolifemedico2' : '',
  subtitle_en: '',
  subtitle_es: '',
  send_at: moment().format(),
});

function PushMessage(props) {
  const { open, onClose, name, id, doctor_schedule } = props;
  const { t } = useTranslation();
  const [record, setRecord] = useState(new PushRecord(initialRecord(doctor_schedule)));
  const [language, setLanguage] = useState('pt');
  const languageObject = {
    pt: '',
    en: '_en',
    es: '_es',
  };

  useEffect(() => handleChange(initialRecord(doctor_schedule)), [doctor_schedule]);
  useEffect(() => setLanguage('pt'), [open]);

  const handleChange = (attrs) => {
    setRecord(record.updateAttributes(attrs));
  };

  async function submitNotification() {
    let image_id = record.get('image_id') ? record.get('image_id') : null;
    const imageBase64 = record.get('image_base64');
    if (imageBase64) {
      try {
        const result = await Request.post('/upload_base64', { file: `data:image/jpg;base64,${imageBase64}` });
        image_id = result.data.id;
      } catch (e) { console.log(e); }
    }

    const data = record.toParams({ image_id });
    Request.post(`/admin/users/${id}/send_push`, data, {}, { controller: 'users' })
      .then(() => {
        toast.success(t('person.toast.push_success'));
        onClose();
      })
      .catch(e => {
        toast.error('person.toast.push_fail');
        onClose();
        console.error(e);
      });
  }

  const handleRadio = event => setLanguage(event.target.value);

  return (
    <Dialog
      icon={<Send />}
      isOpen={open}
      handleClose={onClose}
      title={t('sendNotificationTo', { name })}
      dialogActions={
        <React.Fragment>
          <Button variant='text' color='error' onClick={onClose}>
            <CancelIcon />&nbsp;{t('Cancel')}
          </Button>
          <Button variant="text" color='success' onClick={submitNotification}>
            <Send />&nbsp;{t('Send')}
          </Button>
        </React.Fragment>
      }
    >
      <GridContainer>
        <GridItem xs={12} md={12}>
          <RadioGroup aria-label="position" name="position" value={language} onChange={handleRadio} row>
            <FormControlLabel
              value="pt"
              control={<Radio color="primary" />}
              label="PT-BR"
              labelPlacement="end"
            />
            <FormControlLabel
              value="en"
              control={<Radio color="primary" />}
              label="EN"
              labelPlacement="end"
            />
            <FormControlLabel
              value="es"
              control={<Radio color="primary" />}
              label="ES"
              labelPlacement="end"
            />
          </RadioGroup>
        </GridItem>
        <GridItem xs={8} md={4}>
          <InputControl
            name={`title${languageObject[language]}`}
            value={record.get(`title${languageObject[language]}`)}
            label={attrName(`title${languageObject[language]}`)}
            required={true}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={8} sm={10} md={4}>
          <InputControl
            value={record.get(`subtitle${languageObject[language]}`)}
            name={`subtitle${languageObject[language]}`}
            label={attrName(`subtitle${languageObject[language]}`)}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={8} sm={10} md={3}>
          <InputControl
            value={moment(record.get('send_at')).format()}
            name="send_at"
            required={true}
            label={attrName('send_at')}
            inputComponent={InputDate}
            InputProps={{
              type: 'datetime',
              minDate: moment(),
            }}
            onChange={(date) => setRecord(record.updateAttributes({ send_at: moment(date).format() }))}
          />
        </GridItem>
        <GridItem xs={12} sm={9}>
          <InputControl
            value={record.get(`message${languageObject[language]}`)}
            name={`message${languageObject[language]}`}
            label={attrName(`message${languageObject[language]}`)}
            required={true}
            inputComponent={Input}
            InputProps={{
              multiline: true,
              rows: '4',
            }}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={12} sm={6}>
          <InputControl
            value={record.get('link_url')}
            name="link_url"
            label={attrName('link_url')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={12} sm={6}>
          <InputControl
            value={record.get('image_url')}
            name="image_url"
            label={attrName('image_url')}
            inputComponent={Input}
            onChange={handleChange}
          />
        </GridItem>
        <GridItem xs={8} sm={10} md={8}>
          <InputControl
            value={record.get('image_base64')}
            name="image_base64"
            label={attrName('image_base64')}
            inputComponent={InputDropzone}
            InputProps={{
              image: record.get('image'),
            }}
            onChange={handleChange}
          />
        </GridItem>
      </GridContainer>
    </Dialog>
  );
}

PushMessage.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  name: PropTypes.string,
  id: PropTypes.string,
  doctor_schedule: PropTypes.bool,
};

export default PushMessage;
